import { useState, useEffect, useRef } from 'react'
import { Link } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ChevronRight, RefreshCw, CheckCircle, XCircle, AlertCircle,
  ExternalLink, Link2, Calendar, User, Download, Settings, Lock,
} from 'lucide-react'
import { api } from '@/lib/api/client'
import { BrandGlyph } from './IntegrationsPage'
import type {
  FortnoxStatus, FortnoxArticle, FortnoxCustomer, FortnoxProject,
  FortnoxInvoice, FortnoxInvoiceRow, FortnoxAccount, SyncLogEntry, PaginatedResponse,
} from '@/types/api'

const GLYPH = { monogram: 'FX', gradient: 'linear-gradient(135deg, oklch(0.6 0.14 158) 0%, oklch(0.5 0.13 162) 100%)' }

type Tab = 'articles' | 'customers' | 'projects' | 'invoices' | 'accounts' | 'log' | 'mapping'

export default function FortnoxPage() {
  const queryClient = useQueryClient()

  const [cbSuccess]        = useState(() => new URLSearchParams(window.location.search).get('connected') === 'true')
  const [cbPayrollSuccess] = useState(() => new URLSearchParams(window.location.search).get('payroll_connected') === 'true')
  const [cbError]          = useState(() => new URLSearchParams(window.location.search).get('error'))
  const [cbDetail]         = useState(() => new URLSearchParams(window.location.search).get('detail'))
  const [tab,    setTab]    = useState<Tab>('invoices')
  const [search, setSearch] = useState('')

  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn:  () => api.get<{ roles: string[] }>('/me'),
    staleTime: 5 * 60 * 1000,
  })
  const isPayroll = me?.roles.includes('Payroll') ?? false

  const { data: status, isLoading: statusLoading } = useQuery({
    queryKey: ['fortnox', 'status'],
    queryFn:  () => api.get<FortnoxStatus>('/fortnox/status'),
  })
  const { data: articles = [] } = useQuery({
    queryKey: ['fortnox', 'articles'],
    queryFn:  () => api.get<FortnoxArticle[]>('/fortnox/articles'),
    enabled:  status?.connected === true,
  })
  const { data: customers = [] } = useQuery({
    queryKey: ['fortnox', 'customers'],
    queryFn:  () => api.get<FortnoxCustomer[]>('/fortnox/customers'),
    enabled:  status?.connected === true,
    staleTime: 5 * 60 * 1000,
  })
  const { data: projects = [] } = useQuery({
    queryKey: ['fortnox', 'projects'],
    queryFn:  () => api.get<FortnoxProject[]>('/fortnox/projects'),
    enabled:  status?.connected === true,
    staleTime: 5 * 60 * 1000,
  })
  const { data: invoicesPage } = useQuery({
    queryKey: ['fortnox', 'invoices'],
    queryFn:  () => api.get<PaginatedResponse<FortnoxInvoice>>('/fortnox/invoices'),
    enabled:  status?.connected === true,
  })
  const invoices = invoicesPage?.items ?? []
  const { data: accounts = [] } = useQuery({
    queryKey: ['fortnox', 'accounts'],
    queryFn:  () => api.get<FortnoxAccount[]>('/fortnox/accounts'),
    enabled:  status?.connected === true,
  })
  const { data: syncLog = [] } = useQuery({
    queryKey: ['fortnox', 'sync-log'],
    queryFn:  () => api.get<SyncLogEntry[]>('/fortnox/sync-log'),
    enabled:  status?.connected === true,
  })

  const connectMutation = useMutation({
    mutationFn: () => api.get<{ authUrl: string }>('/fortnox/auth'),
    onSuccess:  (data) => { window.location.href = data.authUrl },
  })
  const disconnectMutation = useMutation({
    mutationFn: () => api.delete<unknown>('/fortnox/connect'),
    onSuccess:  () => queryClient.invalidateQueries({ queryKey: ['fortnox'] }),
  })
  const [syncFromDate,  setSyncFromDate]  = useState('')
  const [syncToDate,    setSyncToDate]    = useState('')
  const [activePreset,  setActivePreset]  = useState<string | null>(null)
  const [rowFillActive, setRowFillActive] = useState(false)
  const [rowFillTotal,  setRowFillTotal]  = useState(0)
  const [rowFillDone,   setRowFillDone]   = useState(0)
  const rowFillAbort = useRef(false)

  const setYearPreset = (year: number) => {
    const label = String(year)
    if (activePreset === label) {
      setSyncFromDate(''); setSyncToDate(''); setActivePreset(null)
    } else {
      setSyncFromDate(`${year}-01-01`); setSyncToDate(`${year}-12-31`); setActivePreset(label)
    }
  }

  const runRowFill = async (fromDate: string, toDate: string, total: number) => {
    rowFillAbort.current = false
    setRowFillActive(true)
    setRowFillTotal(total)
    setRowFillDone(0)
    try {
      while (!rowFillAbort.current) {
        const res = await api.post<{ processed: number; remaining: number }>(
          '/fortnox/fill-invoice-rows',
          { fromDate, toDate, batchSize: 50 },
        )
        setRowFillDone(total - res.remaining)
        if (res.remaining === 0) break
        if (res.processed === 0) break  // token error or all fetches failed — stop to avoid infinite loop
      }
    } finally {
      setRowFillActive(false)
      queryClient.invalidateQueries({ queryKey: ['fortnox'] })
    }
  }

  const syncMutation = useMutation({
    mutationFn: () => {
      const body: Record<string, string> = {}
      if (syncFromDate) body.fromDate = syncFromDate
      if (syncToDate)   body.toDate   = syncToDate
      return api.post<{ rowsNeeded?: number }>('/fortnox/sync', body)
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['fortnox'] })
      if ((data.rowsNeeded ?? 0) > 0 && syncFromDate) {
        runRowFill(syncFromDate, syncToDate, data.rowsNeeded!)
      }
    },
  })

  const payrollConnectMutation = useMutation({
    mutationFn: () => api.get<{ authUrl: string }>('/fortnox/payroll-auth'),
    onSuccess:  (data) => { window.location.href = data.authUrl },
  })
  const payrollDisconnectMutation = useMutation({
    mutationFn: () => api.delete<unknown>('/fortnox/payroll-connect'),
    onSuccess:  () => queryClient.invalidateQueries({ queryKey: ['fortnox', 'status'] }),
  })

  useEffect(() => {
    if (cbSuccess || cbPayrollSuccess || cbError) {
      if (cbSuccess || cbPayrollSuccess) queryClient.invalidateQueries({ queryKey: ['fortnox', 'status'] })
      window.history.replaceState({}, '', window.location.pathname)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleDisconnect = () => {
    if (!confirm('Disconnect Fortnox? The sync will stop and tokens will be deleted.')) return
    disconnectMutation.mutate()
  }

  if (statusLoading) {
    return <div className="page"><Breadcrumb /><p className="muted" style={{ paddingTop: 32, textAlign: 'center' }}>Loading…</p></div>
  }

  // ── Not connected ──────────────────────────────────────────────────────────
  if (!status?.connected) {
    return (
      <div className="page">
        <Breadcrumb />
        {cbError && (
          <div style={{ background: 'var(--ns-err-soft)', border: '1px solid var(--ns-err)', borderRadius: 8, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <AlertCircle size={14} style={{ color: 'var(--ns-err)', flexShrink: 0 }} />
            <span style={{ fontSize: 13, color: 'var(--ns-err)' }}>
              Connection failed: {cbError.replace(/_/g, ' ')}.
              {cbDetail && <span className="mono" style={{ display: 'block', fontSize: 11.5, marginTop: 4, opacity: 0.8 }}>{decodeURIComponent(cbDetail)}</span>}
            </span>
          </div>
        )}
        <div className="card" style={{ maxWidth: 520, padding: 32, textAlign: 'center' }}>
          <div style={{ margin: '0 auto 16px', width: 48 }}><BrandGlyph {...GLYPH} size={48} /></div>
          <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Connect Fortnox</h2>
          <p className="muted" style={{ fontSize: 13.5, lineHeight: 1.6, marginBottom: 24 }}>
            Sync articles and customers from your Fortnox account. You'll be redirected to Fortnox to authorise the connection.
          </p>
          <div style={{ background: 'var(--ns-surface-2)', borderRadius: 8, padding: 16, marginBottom: 24, textAlign: 'left' }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: 'var(--ns-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Before you connect</div>
            <ol style={{ fontSize: 13, lineHeight: 1.8, paddingLeft: 18 }}>
              <li>Register an app at <a href="https://developer.fortnox.se" target="_blank" rel="noreferrer" style={{ color: 'var(--ns-brand-strong)' }}>developer.fortnox.se <ExternalLink size={11} style={{ display: 'inline' }} /></a></li>
              <li>Set scopes: {['article','customer','invoice','bookkeeping','payment','project'].map(s => <span key={s} className="badge" style={{ fontSize: 11, marginRight: 3 }}>{s}</span>)}</li>
              <li>Set redirect URI to the value in your Function App settings</li>
              <li>Add <code style={{ fontSize: 12 }}>FORTNOX_CLIENT_ID</code> and <code style={{ fontSize: 12 }}>FORTNOX_CLIENT_SECRET</code></li>
            </ol>
          </div>
          <button className="btn btn-brand" style={{ gap: 8, width: '100%', justifyContent: 'center', height: 40 }}
            onClick={() => connectMutation.mutate()} disabled={connectMutation.isPending}>
            {connectMutation.isPending
              ? <><RefreshCw size={14} style={{ animation: 'spin 0.8s linear infinite' }} />Redirecting to Fortnox…</>
              : <>Connect to Fortnox</>
            }
          </button>
          {connectMutation.isError && <p style={{ color: 'var(--ns-err)', fontSize: 12.5, marginTop: 10 }}>Failed to start — check FORTNOX_CLIENT_ID and FORTNOX_REDIRECT_URI.</p>}
        </div>
      </div>
    )
  }

  // ── Connected ──────────────────────────────────────────────────────────────
  const lastSyncTime = status.lastSync ? new Date(status.lastSync).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' }) : null
  const lastSyncDate = status.lastSync ? new Date(status.lastSync).toLocaleDateString('sv-SE') : null

  const filteredArticles  = articles.filter(a =>
    a.description.toLowerCase().includes(search.toLowerCase()) || a.articleNumber.includes(search))
  const filteredCustomers = customers.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    (c.fortnoxCustomerNumber ?? '').includes(search))
  const filteredProjects  = projects.filter(p =>
    p.description.toLowerCase().includes(search.toLowerCase()) || p.projectNumber.includes(search))
  const filteredInvoices  = invoices.filter(i => {
    const q = search.toLowerCase()
    return (
      i.documentNumber.toLowerCase().includes(q) ||
      (i.customerName ?? '').toLowerCase().includes(q) ||
      (i.customerNumber ?? '').toLowerCase().includes(q) ||
      (i.ocr ?? '').toLowerCase().includes(q)
    )
  })
  const filteredAccounts  = accounts.filter(a => {
    const q = search.toLowerCase()
    return (
      String(a.number).includes(q) ||
      (a.description ?? '').toLowerCase().includes(q)
    )
  })

  return (
    <div className="page">
      <Breadcrumb />

      {/* Hero header */}
      <div className="card" style={{
        padding: 24, marginBottom: 20,
        background: 'linear-gradient(135deg, var(--ns-surface) 0%, var(--ns-surface-2) 100%)',
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: -40, right: -40, width: 200, height: 200, background: 'radial-gradient(circle, var(--ns-brand-soft) 0%, transparent 70%)', opacity: 0.6, pointerEvents: 'none' }} />
        <div className="row" style={{ gap: 18, alignItems: 'flex-start', position: 'relative' }}>
          <BrandGlyph {...GLYPH} size={56} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="row" style={{ gap: 10, alignItems: 'center', marginBottom: 4 }}>
              <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600, letterSpacing: '-0.015em' }}>Fortnox</h1>
              <span className="badge badge-ok"><span className="dot dot-ok" />Connected</span>
            </div>
            <p className="muted" style={{ margin: 0, fontSize: 13, lineHeight: 1.45 }}>
              Articles, customers, invoices, vouchers and payments. Synced every 15 min.
            </p>
            <div className="row" style={{ gap: 12, marginTop: 12, fontSize: 11.5, color: 'var(--ns-text-muted)' }}>
              <span className="row" style={{ gap: 5 }}><Link2 size={12} />api.fortnox.se</span>
              {status.lastSync && <>
                <span style={{ width: 1, height: 12, background: 'var(--ns-border)' }} />
                <span className="row" style={{ gap: 5 }}><Calendar size={12} />Synced {lastSyncDate}</span>
              </>}
            </div>
          </div>
          <div style={{ flexShrink: 0 }}>
            <div className="row" style={{ gap: 6, marginBottom: 6 }}>
              <button className="btn btn-ghost btn-sm" style={{ gap: 5 }}><Settings size={13} />Configure</button>
              <button className="btn btn-ghost btn-sm" style={{ gap: 5, color: 'var(--ns-err)' }}
                onClick={handleDisconnect} disabled={disconnectMutation.isPending}>
                <Link2 size={13} />Disconnect
              </button>
            </div>
            <div className="row" style={{ gap: 4, alignItems: 'center' }}>
              <span className="muted" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>Invoice rows:</span>
              {[2022, 2023, 2024, 2025].map(y => (
                <button key={y}
                  className={`chip${activePreset === String(y) ? ' active' : ''}`}
                  onClick={() => setYearPreset(y)}
                  disabled={syncMutation.isPending}
                  style={{ fontSize: 11, height: 26 }}
                >{y}</button>
              ))}
              <input
                type="date" value={syncFromDate}
                onChange={e => { setSyncFromDate(e.target.value); setActivePreset(null) }}
                disabled={syncMutation.isPending}
                title="Invoice date from (leave blank for incremental sync)"
                style={{ fontSize: 12, padding: '3px 6px', borderRadius: 6, border: '1px solid var(--ns-border)', background: 'var(--ns-surface-2)', color: 'var(--ns-text)', height: 26, width: 124 }}
              />
              <span className="muted" style={{ fontSize: 12 }}>–</span>
              <input
                type="date" value={syncToDate}
                onChange={e => { setSyncToDate(e.target.value); setActivePreset(null) }}
                disabled={syncMutation.isPending}
                title="Invoice date to"
                style={{ fontSize: 12, padding: '3px 6px', borderRadius: 6, border: '1px solid var(--ns-border)', background: 'var(--ns-surface-2)', color: 'var(--ns-text)', height: 26, width: 124 }}
              />
              <button className="btn btn-brand btn-sm" style={{ gap: 5 }}
                onClick={() => syncMutation.mutate()} disabled={syncMutation.isPending || rowFillActive}>
                <RefreshCw size={13} style={{ animation: syncMutation.isPending ? 'spin 0.8s linear infinite' : undefined }} />
                {syncMutation.isPending ? 'Syncing…' : syncFromDate || syncToDate ? 'Sync invoices' : 'Sync now'}
              </button>
            </div>

            {/* Row fill progress — shown while filling or after completion */}
            {(rowFillActive || rowFillDone > 0) && (
              <div className="row" style={{ gap: 8, alignItems: 'center', marginTop: 8, padding: '6px 10px', background: 'var(--ns-surface-2)', borderRadius: 8, border: '1px solid var(--ns-border)' }}>
                <span style={{ fontSize: 12, color: 'var(--ns-text-muted)', whiteSpace: 'nowrap' }}>
                  {rowFillActive ? 'Filling invoice rows' : '✓ Invoice rows synced'}
                </span>
                <div style={{ flex: 1, height: 6, background: 'var(--ns-border)', borderRadius: 3, overflow: 'hidden', minWidth: 80 }}>
                  <div style={{ height: '100%', width: `${rowFillTotal > 0 ? Math.round((rowFillDone / rowFillTotal) * 100) : 0}%`, background: 'var(--ns-brand)', borderRadius: 3, transition: 'width 0.3s ease' }} />
                </div>
                <span style={{ fontSize: 12, fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }}>
                  {rowFillDone} / {rowFillTotal}
                </span>
                {rowFillActive ? (
                  <button className="btn btn-sm" style={{ fontSize: 11, height: 24, padding: '0 8px' }}
                    onClick={() => { rowFillAbort.current = true }}>
                    Stop
                  </button>
                ) : (
                  <button className="btn btn-sm" style={{ fontSize: 11, height: 24, padding: '0 8px' }}
                    onClick={() => setRowFillDone(0)}>
                    Dismiss
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Stat row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 22 }}>
        <DetailStat label="Invoices"         value={String(status.invoiceCount)}  sub={`${status.invoiceRowCount} rows synced`}      trend={[2,3,3,4,4,4,5]} />
        <DetailStat label="Customers"        value={String(status.customerCount)} sub="Synced from Fortnox"                            trend={[1,1,2,2,3,3,4]} />
        <DetailStat label="Active articles"  value={String(status.articleCount)}  sub={`of ${articles.length} total`}                  trend={[1,2,2,3,3,3,4]} />
        <DetailStat label="Last sync"        value={lastSyncTime ?? '—'}          sub={lastSyncDate ?? 'Never'}              />
        <DetailStat label="Sync status"      isCustom
          value={<span className="badge badge-ok" style={{ fontSize: 13, height: 26, padding: '0 10px' }}><span className="dot dot-ok" />Healthy</span>}
          sub="Every 15 min" />
      </div>

      {/* Payroll connection — visible only to Payroll role */}
      {isPayroll && (
        <div className="card" style={{ padding: 20, marginBottom: 20 }}>
          <div className="row" style={{ gap: 14, alignItems: 'flex-start' }}>
            <div style={{
              width: 36, height: 36, borderRadius: 8, flexShrink: 0,
              background: status?.payrollConnected ? 'oklch(0.88 0.08 145)' : 'var(--ns-surface-3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Lock size={16} style={{ color: status?.payrollConnected ? 'oklch(0.45 0.12 145)' : 'var(--ns-text-muted)' }} />
            </div>
            <div style={{ flex: 1 }}>
              <div className="row" style={{ gap: 8, alignItems: 'center', marginBottom: 3 }}>
                <span style={{ fontSize: 14, fontWeight: 600 }}>Payroll connection</span>
                {status?.payrollConnected
                  ? <span className="badge badge-ok" style={{ fontSize: 11 }}>Connected</span>
                  : <span className="badge"          style={{ fontSize: 11 }}>Not connected</span>
                }
              </div>
              <p className="muted" style={{ fontSize: 12.5, lineHeight: 1.5, margin: 0 }}>
                Separate salary-only Fortnox OAuth connection. Used exclusively by payroll sync — the main Fortnox token has no salary scope.
                Only users with the <strong>Payroll</strong> role can connect, disconnect, or trigger sync.
              </p>
              {cbPayrollSuccess && (
                <p style={{ fontSize: 12.5, color: 'var(--ns-ok)', marginTop: 6 }}>Payroll connection authorised successfully.</p>
              )}
            </div>
            <div className="row" style={{ gap: 6, flexShrink: 0 }}>
              {status?.payrollConnected ? (
                <button
                  className="btn btn-ghost btn-sm"
                  style={{ gap: 5, color: 'var(--ns-err)' }}
                  onClick={() => { if (confirm('Disconnect Fortnox payroll? Payroll sync will stop until reconnected.')) payrollDisconnectMutation.mutate() }}
                  disabled={payrollDisconnectMutation.isPending}
                >
                  <Link2 size={13} />Disconnect payroll
                </button>
              ) : (
                <button
                  className="btn btn-brand btn-sm"
                  style={{ gap: 5 }}
                  onClick={() => payrollConnectMutation.mutate()}
                  disabled={payrollConnectMutation.isPending}
                >
                  {payrollConnectMutation.isPending
                    ? <><RefreshCw size={13} style={{ animation: 'spin 0.8s linear infinite' }} />Redirecting…</>
                    : <>Connect payroll</>
                  }
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="tabs" style={{ marginBottom: 0 }}>
        {([
          ['invoices',  'Invoices',      status.invoiceCount],
          ['customers', 'Customers',     status.customerCount],
          ['articles',  'Articles',      status.articleCount],
          ['projects',  'Projects',      status.projectCount],
          ['accounts',  'Accounts',      status.accountCount],
          ['log',       'Sync log',      null],
          ['mapping',   'Field mapping', null],
        ] as [Tab, string, number | null][]).map(([id, label, count]) => (
          <button key={id} className={`tab${tab === id ? ' active' : ''}`} onClick={() => { setTab(id); setSearch('') }}>
            {label}{count !== null && <span className="count">{count}</span>}
          </button>
        ))}
      </div>

      <div style={{ marginTop: 18 }}>
        {tab === 'articles'  && <ArticlesTab  rows={filteredArticles}  search={search} onSearch={setSearch} total={articles.length} />}
        {tab === 'customers' && <CustomersTab rows={filteredCustomers} search={search} onSearch={setSearch} total={customers.length} />}
        {tab === 'projects'  && <ProjectsTab  rows={filteredProjects}  search={search} onSearch={setSearch} total={projects.length} />}
        {tab === 'invoices'  && <InvoicesTab  rows={filteredInvoices}  search={search} onSearch={setSearch} total={invoicesPage?.total ?? invoices.length} />}
        {tab === 'accounts'  && <AccountsTab  rows={filteredAccounts}  search={search} onSearch={setSearch} total={accounts.length} />}
        {tab === 'log'       && <SyncLogTab   log={syncLog} />}
        {tab === 'mapping'   && <MappingTab />}
      </div>
    </div>
  )
}

// ── Articles tab ───────────────────────────────────────────────────────────

function ArticlesTab({ rows, search, onSearch, total }: {
  rows: FortnoxArticle[]; search: string; onSearch: (v: string) => void; total: number
}) {
  return (
    <div className="card" style={{ padding: 0 }}>
      <div className="row" style={{ padding: '12px 14px', borderBottom: '1px solid var(--ns-border)', gap: 8 }}>
        <SearchInput value={search} onChange={onSearch} placeholder="Search articles…" width={260} />
        <div style={{ flex: 1 }} />
        <span className="muted tabular" style={{ fontSize: 11.5 }}>Showing {rows.length} of {total}</span>
        <button className="btn btn-ghost btn-sm"><Download size={13} />Export</button>
      </div>
      <table className="table">
        <thead>
          <tr>
            <th style={{ width: 80 }}>#</th>
            <th>Description</th>
            <th style={{ width: 70 }}>Unit</th>
            <th style={{ width: 150, textAlign: 'right' }}>Price</th>
            <th style={{ width: 80 }}>Price list</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(a => (
            <tr key={a.articleNumber}>
              <td className="mono muted" style={{ fontSize: 12 }}>{a.articleNumber}</td>
              <td style={{ fontWeight: 500 }}>{a.description}</td>
              <td>{a.unit ? <span className="kbd" style={{ fontSize: 10.5 }}>{a.unit}</span> : <span className="muted">—</span>}</td>
              <td className="mono tabular" style={{ textAlign: 'right' }}>
                {a.price && a.price > 0
                  ? <>{a.price.toLocaleString('sv-SE')} <span style={{ color: 'var(--ns-text-subtle)', fontSize: 11 }}>kr</span></>
                  : <span className="muted">—</span>
                }
              </td>
              <td className="muted" style={{ fontSize: 12 }}>{a.priceList ?? '—'}</td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={5} className="muted" style={{ textAlign: 'center', padding: 24 }}>No articles found</td></tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

// ── Customers tab ──────────────────────────────────────────────────────────

function CustomersTab({ rows, search, onSearch, total }: {
  rows: FortnoxCustomer[]; search: string; onSearch: (v: string) => void; total: number
}) {
  return (
    <div className="card" style={{ padding: 0 }}>
      <div className="row" style={{ padding: '12px 14px', borderBottom: '1px solid var(--ns-border)', gap: 8 }}>
        <SearchInput value={search} onChange={onSearch} placeholder="Search customers…" width={260} />
        <div style={{ flex: 1 }} />
        <span className="muted tabular" style={{ fontSize: 11.5 }}>Showing {rows.length} of {total}</span>
      </div>
      <table className="table">
        <thead>
          <tr>
            <th style={{ width: 100 }}>Fortnox #</th>
            <th>Name</th>
            <th style={{ width: 90, textAlign: 'center' }}>Active</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(c => (
            <tr key={c.id}>
              <td className="mono muted" style={{ fontSize: 12 }}>{c.fortnoxCustomerNumber ?? '—'}</td>
              <td>
                <div className="row" style={{ gap: 8 }}>
                  <CustomerChip name={c.name} num={c.id} />
                  <span style={{ fontWeight: 500 }}>{c.name}</span>
                </div>
              </td>
              <td style={{ textAlign: 'center' }}>
                {c.isActive
                  ? <CheckCircle size={14} style={{ color: 'var(--ns-ok)' }} />
                  : <XCircle    size={14} style={{ color: 'var(--ns-err)' }} />
                }
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={3} className="muted" style={{ textAlign: 'center', padding: 24 }}>No customers synced yet</td></tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

// ── Projects tab ──────────────────────────────────────────────────────────

function ProjectsTab({ rows, search, onSearch, total }: {
  rows: FortnoxProject[]; search: string; onSearch: (v: string) => void; total: number
}) {
  const STATUS_LABEL: Record<string, string> = {
    ONGOING:    'Ongoing',
    NOTSTARTED: 'Not started',
    COMPLETED:  'Completed',
  }
  const STATUS_CLASS: Record<string, string> = {
    ONGOING:    'badge-ok',
    NOTSTARTED: 'badge-warn',
    COMPLETED:  '',
  }
  return (
    <div className="card" style={{ padding: 0 }}>
      <div className="row" style={{ padding: '12px 14px', borderBottom: '1px solid var(--ns-border)', gap: 8 }}>
        <SearchInput value={search} onChange={onSearch} placeholder="Search projects…" width={260} />
        <div style={{ flex: 1 }} />
        <span className="muted tabular" style={{ fontSize: 11.5 }}>Showing {rows.length} of {total}</span>
      </div>
      <table className="table">
        <thead>
          <tr>
            <th style={{ width: 90 }}>#</th>
            <th>Description</th>
            <th style={{ width: 110 }}>Status</th>
            <th style={{ width: 100 }}>Start</th>
            <th style={{ width: 100 }}>End</th>
            <th style={{ width: 160 }}>Forge project</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(p => (
            <tr key={p.projectNumber}>
              <td className="mono muted" style={{ fontSize: 12 }}>{p.projectNumber}</td>
              <td style={{ fontWeight: 500 }}>{p.description}</td>
              <td>
                {p.status
                  ? <span className={`badge ${STATUS_CLASS[p.status] ?? ''}`} style={{ fontSize: 11 }}>
                      {STATUS_LABEL[p.status] ?? p.status}
                    </span>
                  : <span className="muted">—</span>
                }
              </td>
              <td className="mono tabular muted" style={{ fontSize: 12 }}>{p.startDate ?? '—'}</td>
              <td className="mono tabular muted" style={{ fontSize: 12 }}>{p.endDate ?? '—'}</td>
              <td>
                {p.coreProjectName
                  ? <span style={{ fontSize: 12, color: 'var(--ns-brand-strong)', fontWeight: 500 }}>{p.coreProjectName}</span>
                  : <span className="muted" style={{ fontSize: 12 }}>Not linked</span>
                }
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={6} className="muted" style={{ textAlign: 'center', padding: 24 }}>No projects synced yet</td></tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

// ── Invoices tab ───────────────────────────────────────────────────────────

const INVOICE_STATUS_CLASS: Record<string, string> = {
  paid:      'badge-ok',
  sent:      'badge-warn',
  draft:     '',
  cancelled: 'badge-err',
}

function InvoicesTab({ rows, search, onSearch, total }: {
  rows: FortnoxInvoice[]; search: string; onSearch: (v: string) => void; total: number
}) {
  const [expanded, setExpanded] = useState<string | null>(null)
  return (
    <div className="card" style={{ padding: 0 }}>
      <div className="row" style={{ padding: '12px 14px', borderBottom: '1px solid var(--ns-border)', gap: 8 }}>
        <SearchInput value={search} onChange={onSearch} placeholder="Search by #, customer or OCR…" width={300} />
        <div style={{ flex: 1 }} />
        <span className="muted tabular" style={{ fontSize: 11.5 }}>Showing {rows.length} of {total}</span>
      </div>
      <table className="table">
        <thead>
          <tr>
            <th style={{ width: 90 }}>#</th>
            <th style={{ width: 100 }}>Date</th>
            <th>Customer</th>
            <th style={{ width: 90 }}>Status</th>
            <th style={{ width: 130, textAlign: 'right' }}>Total</th>
            <th style={{ width: 130, textAlign: 'right' }}>Balance</th>
            <th style={{ width: 70 }}>Currency</th>
            <th style={{ width: 70, textAlign: 'right' }}>Rows</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(i => {
            const isOpen = expanded === i.documentNumber
            return (
              <React.Fragment key={i.documentNumber}>
                <tr style={{ cursor: 'pointer' }} onClick={() => setExpanded(isOpen ? null : i.documentNumber)}>
                  <td className="mono" style={{ fontSize: 12, fontWeight: 500 }}>{i.documentNumber}</td>
                  <td className="mono tabular muted" style={{ fontSize: 12 }}>{i.invoiceDate ?? '—'}</td>
                  <td>
                    <div style={{ fontWeight: 500 }}>{i.customerName ?? '—'}</div>
                    {i.customerNumber && <div className="mono muted" style={{ fontSize: 11 }}>{i.customerNumber}</div>}
                  </td>
                  <td>
                    {i.status
                      ? <span className={`badge ${INVOICE_STATUS_CLASS[i.status] ?? ''}`} style={{ fontSize: 11 }}>{i.status}</span>
                      : <span className="muted">—</span>}
                  </td>
                  <td className="mono tabular" style={{ textAlign: 'right' }}>{formatMoney(i.total, i.currency)}</td>
                  <td className="mono tabular" style={{ textAlign: 'right', color: (i.balance ?? 0) > 0 ? 'var(--ns-err)' : undefined }}>
                    {formatMoney(i.balance, i.currency)}
                  </td>
                  <td className="muted" style={{ fontSize: 12 }}>{i.currency ?? '—'}</td>
                  <td className="mono tabular" style={{ textAlign: 'right', fontSize: 12 }}>{i.rowCount}</td>
                </tr>
                {isOpen && (
                  <tr>
                    <td colSpan={8} style={{ background: 'var(--ns-surface-2)', padding: 0 }}>
                      <InvoiceRowsExpansion documentNumber={i.documentNumber} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            )
          })}
          {rows.length === 0 && (
            <tr><td colSpan={8} className="muted" style={{ textAlign: 'center', padding: 24 }}>No invoices synced yet</td></tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

function InvoiceRowsExpansion({ documentNumber }: { documentNumber: string }) {
  const { data: rows = [], isLoading, isError } = useQuery({
    queryKey: ['fortnox', 'invoices', documentNumber, 'rows'],
    queryFn:  () => api.get<FortnoxInvoiceRow[]>(`/fortnox/invoices/${documentNumber}/rows`),
  })

  if (isLoading) return <div className="muted" style={{ padding: 16, fontSize: 12 }}>Loading rows…</div>
  if (isError)   return <div style={{ padding: 16, fontSize: 12, color: 'var(--ns-err)' }}>Failed to load rows</div>
  if (rows.length === 0) return <div className="muted" style={{ padding: 16, fontSize: 12 }}>No rows for this invoice</div>

  return (
    <table className="table" style={{ margin: 0 }}>
      <thead>
        <tr>
          <th style={{ width: 50 }}>#</th>
          <th style={{ width: 80 }}>Account</th>
          <th style={{ width: 90 }}>Article</th>
          <th>Description</th>
          <th style={{ width: 80, textAlign: 'right' }}>Qty</th>
          <th style={{ width: 60 }}>Unit</th>
          <th style={{ width: 110, textAlign: 'right' }}>Price</th>
          <th style={{ width: 70, textAlign: 'right' }}>Disc.</th>
          <th style={{ width: 60, textAlign: 'right' }}>VAT %</th>
          <th style={{ width: 120, textAlign: 'right' }}>Total ex VAT</th>
          <th style={{ width: 120, textAlign: 'right' }}>Total</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(r => (
          <tr key={r.rowId}>
            <td className="mono muted" style={{ fontSize: 11 }}>{r.rowId}</td>
            <td className="mono muted" style={{ fontSize: 11 }}>{r.accountNumber ?? '—'}</td>
            <td className="mono muted" style={{ fontSize: 11 }}>{r.articleNumber ?? '—'}</td>
            <td style={{ fontSize: 12.5 }}>{r.description ?? '—'}</td>
            <td className="mono tabular" style={{ textAlign: 'right', fontSize: 12 }}>{r.deliveredQuantity ?? '—'}</td>
            <td className="muted" style={{ fontSize: 11 }}>{r.unit ?? '—'}</td>
            <td className="mono tabular" style={{ textAlign: 'right', fontSize: 12 }}>{formatMoney(r.price, null)}</td>
            <td className="mono tabular muted" style={{ textAlign: 'right', fontSize: 12 }}>
              {r.discount ? (r.discountType === 'PERCENT' ? `${r.discount}%` : formatMoney(r.discount, null)) : '—'}
            </td>
            <td className="mono tabular muted" style={{ textAlign: 'right', fontSize: 12 }}>{r.vat ?? '—'}</td>
            <td className="mono tabular" style={{ textAlign: 'right', fontSize: 12 }}>{formatMoney(r.totalExcludingVat, null)}</td>
            <td className="mono tabular" style={{ textAlign: 'right', fontSize: 12, fontWeight: 500 }}>{formatMoney(r.total, null)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

// ── Accounts tab ───────────────────────────────────────────────────────────

function AccountsTab({ rows, search, onSearch, total }: {
  rows: FortnoxAccount[]; search: string; onSearch: (v: string) => void; total: number
}) {
  const [yearFilter, setYearFilter] = useState<number | 'all'>('all')
  const years = Array.from(new Set(rows.map(r => r.year))).sort((a, b) => b - a)
  const filtered = yearFilter === 'all' ? rows : rows.filter(r => r.year === yearFilter)

  return (
    <div className="card" style={{ padding: 0 }}>
      <div className="row" style={{ padding: '12px 14px', borderBottom: '1px solid var(--ns-border)', gap: 8 }}>
        <SearchInput value={search} onChange={onSearch} placeholder="Search by number or description…" width={300} />
        <div className="row" style={{ gap: 4 }}>
          <button className={`chip${yearFilter === 'all' ? ' active' : ''}`} onClick={() => setYearFilter('all')}>All years</button>
          {years.map(y => (
            <button key={y} className={`chip${yearFilter === y ? ' active' : ''}`} onClick={() => setYearFilter(y)}>{y}</button>
          ))}
        </div>
        <div style={{ flex: 1 }} />
        <span className="muted tabular" style={{ fontSize: 11.5 }}>Showing {filtered.length} of {total}</span>
      </div>
      <table className="table">
        <thead>
          <tr>
            <th style={{ width: 80 }}>Number</th>
            <th>Description</th>
            <th style={{ width: 70 }}>Year</th>
            <th style={{ width: 70, textAlign: 'center' }}>Active</th>
            <th style={{ width: 80 }}>VAT</th>
            <th style={{ width: 140, textAlign: 'right' }}>Brought forward</th>
            <th style={{ width: 140, textAlign: 'right' }}>Carried forward</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map(a => (
            <tr key={`${a.number}-${a.year}`}>
              <td className="mono" style={{ fontSize: 12, fontWeight: 500 }}>{a.number}</td>
              <td>{a.description ?? '—'}</td>
              <td className="mono tabular muted" style={{ fontSize: 12 }}>{a.year}</td>
              <td style={{ textAlign: 'center' }}>
                {a.active
                  ? <CheckCircle size={14} style={{ color: 'var(--ns-ok)' }} />
                  : <XCircle    size={14} style={{ color: 'var(--ns-err)' }} />}
              </td>
              <td className="mono muted" style={{ fontSize: 11 }}>{a.vatCode ?? '—'}</td>
              <td className="mono tabular" style={{ textAlign: 'right', fontSize: 12 }}>{formatMoney(a.balanceBroughtForward, null)}</td>
              <td className="mono tabular" style={{ textAlign: 'right', fontSize: 12 }}>{formatMoney(a.balanceCarriedForward, null)}</td>
            </tr>
          ))}
          {filtered.length === 0 && (
            <tr><td colSpan={7} className="muted" style={{ textAlign: 'center', padding: 24 }}>No accounts synced yet</td></tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

function formatMoney(v: number | null | undefined, currency: string | null | undefined): string {
  if (v == null) return '—'
  const formatted = v.toLocaleString('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return currency ? `${formatted} ${currency}` : formatted
}

// ── Sync log tab ───────────────────────────────────────────────────────────

function SyncLogTab({ log }: { log: SyncLogEntry[] }) {
  const [entityFilter, setEntityFilter] = useState<string>('all')

  const entities = Array.from(new Set(log.map(l => l.entity))).sort()
  const filtered  = entityFilter === 'all' ? log : log.filter(l => l.entity === entityFilter)

  const grouped: Record<string, SyncLogEntry[]> = {}
  filtered.forEach(l => {
    const key = new Date(l.syncedAt).toLocaleString('sv-SE', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
    if (!grouped[key]) grouped[key] = []
    grouped[key].push(l)
  })

  // Sync rate sidebar: last 24h
  const cutoff = Date.now() - 24 * 60 * 60 * 1000
  const last24 = log.filter(l => new Date(l.syncedAt).getTime() > cutoff)
  const successCount = last24.filter(l => l.status === 'success').length
  const successRate  = last24.length > 0 ? Math.round((successCount / last24.length) * 100) : 100

  const hourBuckets = Array.from({ length: 24 }, (_, i) => {
    const h = (new Date().getHours() - 23 + i + 24) % 24
    return last24.filter(l => new Date(l.syncedAt).getHours() === h && l.status === 'success').length
  })
  const maxBucket = Math.max(...hourBuckets, 1)

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 16 }}>
      {/* Main log */}
      <div className="card" style={{ padding: 0 }}>
        <div className="row" style={{ padding: '12px 14px', borderBottom: '1px solid var(--ns-border)', gap: 6 }}>
          <button className={`chip${entityFilter === 'all' ? ' active' : ''}`} onClick={() => setEntityFilter('all')}>All</button>
          {entities.map(e => (
            <button key={e} className={`chip${entityFilter === e ? ' active' : ''}`} onClick={() => setEntityFilter(e)}
              style={{ textTransform: 'capitalize' }}>{e}</button>
          ))}
          <div style={{ flex: 1 }} />
          <button className="btn btn-ghost btn-sm"><Download size={13} />Export log</button>
        </div>
        <div style={{ maxHeight: 540, overflow: 'auto' }}>
          {Object.entries(grouped).map(([minute, items]) => (
            <div key={minute}>
              <div style={{
                padding: '8px 14px', background: 'var(--ns-surface-2)',
                fontSize: 10.5, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase',
                color: 'var(--ns-text-muted)', borderTop: '1px solid var(--ns-border)',
                position: 'sticky', top: 0, zIndex: 1,
              }} className="tabular">{minute}</div>
              {items.map(l => (
                <div key={l.id} className="row" style={{ padding: '10px 14px', borderTop: '1px solid var(--ns-border)', gap: 12 }}>
                  {l.status === 'success'
                    ? <CheckCircle size={14} style={{ color: 'var(--ns-ok)', flexShrink: 0 }} />
                    : <AlertCircle size={14} style={{ color: 'var(--ns-err)', flexShrink: 0 }} />
                  }
                  <span style={{ fontSize: 11, padding: '1px 7px', borderRadius: 4, background: 'var(--ns-surface-2)', color: 'var(--ns-text-muted)', border: '1px solid var(--ns-border)', fontWeight: 500, minWidth: 80, textAlign: 'center', flexShrink: 0 }}>
                    {l.entity}
                  </span>
                  <span style={{ flex: 1, fontSize: 12.5 }}>
                    {l.recordsUpserted > 0
                      ? <>Synced <strong className="tabular">{l.recordsUpserted}</strong> records</>
                      : l.errorMessage
                        ? <span style={{ color: 'var(--ns-err)' }}>{l.errorMessage}</span>
                        : <span className="muted">No changes</span>
                    }
                  </span>
                  <span className="mono tabular muted" style={{ fontSize: 11 }}>
                    {new Date(l.syncedAt).toLocaleTimeString('sv-SE')}
                  </span>
                </div>
              ))}
            </div>
          ))}
          {Object.keys(grouped).length === 0 && (
            <div className="muted" style={{ textAlign: 'center', padding: 32 }}>No sync history yet</div>
          )}
        </div>
      </div>

      {/* Sidebar */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div className="card" style={{ padding: 14 }}>
          <div className="muted" style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 10 }}>Sync rate · last 24h</div>
          <div className="mono tabular" style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.02em' }}>
            {successCount}<span style={{ fontSize: 13, color: 'var(--ns-text-muted)', fontWeight: 500 }}>/{last24.length || '—'}</span>
          </div>
          <div className="muted" style={{ fontSize: 11.5, marginTop: 2 }}>{last24.length - successCount} failed · {successRate}% success</div>
          <div className="row" style={{ gap: 2, marginTop: 14, alignItems: 'flex-end', height: 32 }}>
            {hourBuckets.map((v, i) => (
              <div key={i} style={{
                flex: 1, borderRadius: 1,
                height: `${Math.max(10, (v / maxBucket) * 100)}%`,
                background: 'var(--ns-brand-strong)',
                opacity: 0.4 + (i / 24) * 0.6,
              }} />
            ))}
          </div>
          <div className="row" style={{ justifyContent: 'space-between', marginTop: 6, fontSize: 10, color: 'var(--ns-text-subtle)' }}>
            <span>24h ago</span><span>12h ago</span><span>now</span>
          </div>
        </div>

        <div className="card" style={{ padding: 14 }}>
          <div className="muted" style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 10 }}>Endpoints</div>
          {(['articles', 'customers', 'projects', 'invoices', 'accounts', 'vouchers'] as const).map(entity => {
            const entityLog = log.filter(l => l.entity === entity)
            const hasError  = entityLog.some(l => l.status === 'failed')
            return (
              <div key={entity} className="row" style={{ padding: '6px 0', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 12.5, textTransform: 'capitalize' }}>{entity}</span>
                <span className={`dot ${hasError ? 'dot-warn' : 'dot-ok'}`} style={{ width: 6, height: 6 }} />
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

// ── Field mapping tab ──────────────────────────────────────────────────────

function MappingTab() {
  const sections: { title: string; rows: [string, string][] }[] = [
    {
      title: 'Articles',
      rows: [
        ['ArticleNumber', 'article_number'],
        ['Description',   'description'],
        ['SalesPrice',    'price'],
        ['PurchasePrice', 'purchase_price'],
        ['Unit',          'unit'],
        ['Active',        'is_active'],
      ],
    },
    {
      title: 'Customers',
      rows: [
        ['CustomerNumber', 'fortnox_customer_number'],
        ['Name',           'name'],
        ['OrganisationNumber', 'org_nr'],
        ['Email',          'billing_email'],
        ['Address1',       'street'],
        ['ZipCode',        'postal_code'],
        ['City',           'city'],
        ['Active',         'is_active'],
      ],
    },
    {
      title: 'Projects',
      rows: [
        ['ProjectNumber',  'project_number'],
        ['Description',    'description'],
        ['Status',         'status'],
        ['StartDate',      'start_date'],
        ['EndDate',        'end_date'],
        ['ProjectLeader',  'project_leader'],
      ],
    },
    {
      title: 'Invoices',
      rows: [
        ['DocumentNumber',            'document_number'],
        ['CustomerNumber',            'customer_number'],
        ['CustomerName',              'customer_name'],
        ['Total',                     'total'],
        ['TotalVAT',                  'total_vat'],
        ['Balance',                   'balance'],
        ['Currency',                  'currency'],
        ['InvoiceDate',               'invoice_date'],
        ['DueDate',                   'due_date'],
        ['FinalPayDate',              'final_pay_date'],
        ['Booked / Cancelled / Sent', 'booked / cancelled / sent'],
        ['InvoiceType',               'invoice_type'],
        ['OCR',                       'ocr'],
        ['VoucherNumber/Series/Year', 'voucher_number / voucher_series / voucher_year'],
        ['Project',                   'project'],
        ['(excluded)',                'EDIInformation, EUQuarterlyReport'],
      ],
    },
    {
      title: 'InvoiceRows',
      rows: [
        ['RowId',                'row_id'],
        ['(parent DocumentNumber)', 'voucher_document_number'],
        ['AccountNumber',        'account_number'],
        ['ArticleNumber',        'article_number'],
        ['Description',          'description'],
        ['DeliveredQuantity',    'delivered_quantity'],
        ['Price / PriceExclVAT', 'price / price_excluding_vat'],
        ['Discount / Type',      'discount / discount_type'],
        ['VAT',                  'vat'],
        ['Total / TotalExclVAT', 'total / total_excluding_vat'],
        ['CostCenter',           'cost_center'],
        ['Project',              'project'],
      ],
    },
    {
      title: 'Accounts',
      rows: [
        ['Number',                 'number'],
        ['Year',                   'year'],
        ['Active',                 'active'],
        ['Description',            'description'],
        ['SRU',                    'sru'],
        ['VATCode',                'vat_code'],
        ['BalanceBroughtForward',  'balance_brought_forward'],
        ['BalanceCarriedForward',  'balance_carried_forward'],
        ['CostCenter',             'cost_center'],
        ['Project',                'project'],
      ],
    },
  ]
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {sections.map(s => (
        <div key={s.title} className="card" style={{ padding: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 14 }}>{s.title}</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 24px 1fr', gap: '8px 12px', alignItems: 'center', maxWidth: 600 }}>
            <div style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--ns-text-muted)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Fortnox field</div>
            <div />
            <div style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--ns-text-muted)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Forge column</div>
            {s.rows.map(([from, to], i) => (
              <React.Fragment key={i}>
                <div className="mono" style={{ fontSize: 12, padding: '6px 10px', background: 'var(--ns-surface-2)', borderRadius: 6, border: '1px solid var(--ns-border)' }}>{from}</div>
                <div style={{ textAlign: 'center', color: 'var(--ns-text-subtle)', fontSize: 14 }}>→</div>
                <div className="mono" style={{ fontSize: 12, padding: '6px 10px', background: 'var(--ns-brand-soft)', borderRadius: 6, color: 'var(--ns-brand-strong)' }}>{to}</div>
              </React.Fragment>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

// ── Shared helpers ─────────────────────────────────────────────────────────

function Breadcrumb() {
  return (
    <div className="row" style={{ gap: 4, marginBottom: 14, color: 'var(--ns-text-muted)', fontSize: 12 }}>
      <Link to="/settings/integrations" style={{ color: 'inherit' }}>Settings</Link>
      <ChevronRight size={11} />
      <Link to="/settings/integrations" style={{ color: 'inherit' }}>Integrations</Link>
      <ChevronRight size={11} />
      <span style={{ color: 'var(--ns-text)' }}>Fortnox</span>
    </div>
  )
}

function DetailStat({ label, value, sub, trend, isCustom }: {
  label: string; value?: React.ReactNode; sub?: string
  trend?: number[]; isCustom?: boolean
}) {
  const max = trend ? Math.max(...(trend as number[]), 1) : 1
  return (
    <div className="card" style={{ padding: 16, position: 'relative', overflow: 'hidden' }}>
      <div className="muted" style={{ fontSize: 11, fontWeight: 500, marginBottom: 6, letterSpacing: '0.01em' }}>{label}</div>
      {isCustom
        ? <div style={{ marginTop: 2 }}>{value}</div>
        : <div className="mono tabular" style={{ fontSize: 26, fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1 }}>{value}</div>
      }
      {sub && <div className="muted" style={{ fontSize: 11.5, marginTop: 6 }}>{sub}</div>}
      {trend && (
        <svg width="64" height="20" viewBox="0 0 64 20" style={{ position: 'absolute', bottom: 12, right: 12, opacity: 0.65 }}>
          <polyline fill="none" stroke="var(--ns-brand-strong)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
            points={trend.map((v, i) => `${i * (64 / (trend.length - 1))},${20 - (v / max) * 18}`).join(' ')} />
        </svg>
      )}
    </div>
  )
}

function CustomerChip({ name, num }: { name: string; num: number }) {
  const initials = name.split(' ').filter(w => /^[A-Za-zÅÄÖåäö]/.test(w)).slice(0, 2).map(w => w[0]).join('').toUpperCase()
  const hue = (num * 83) % 360
  return (
    <div style={{
      width: 22, height: 22, borderRadius: 5, flexShrink: 0,
      background: `oklch(${0.55 + (num % 7) * 0.03} 0.12 ${hue})`,
      color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 9.5, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace",
    }}>{initials.slice(0, 2)}</div>
  )
}

function SearchInput({ value, onChange, placeholder, width = 280 }: {
  value: string; onChange: (v: string) => void; placeholder: string; width?: number
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 10, height: 30, border: '1px solid var(--ns-border)', borderRadius: 6, background: 'var(--ns-surface)', width }}>
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--ns-text-subtle)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>
      </svg>
      <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        style={{ flex: 1, height: '100%', border: 'none', background: 'transparent', outline: 'none', fontSize: 13 }} />
    </div>
  )
}

// React is needed for JSX Fragment in MappingTab
import React from 'react'
